// ─────────────────────────────────────────────────────────────────────────────
// FILE 1: src/utils/cacheKeys.js
// Single source of truth for ALL localStorage keys and shapes.
// Import this everywhere — never hardcode key strings again.
// ─────────────────────────────────────────────────────────────────────────────

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 h

/**
 * Write a value into localStorage under a standard envelope.
 * { timestamp: <ms>, data: <value> }
 */
export function cacheWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data: value }));
  } catch (e) {
    console.warn(`cacheWrite(${key}) failed:`, e);
  }
}

/**
 * Read a value from localStorage, respecting TTL.
 * Returns the inner `data` value, or null if missing/expired.
 */
export function cacheRead(key, ttl = CACHE_TTL) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw);

    // Support both shapes that have crept in over time:
    //   new shape → { timestamp, data }
    //   old shape → { ts, d }
    const ts   = envelope.timestamp ?? envelope.ts ?? 0;
    const data = envelope.data      ?? envelope.d  ?? null;

    if (Date.now() - ts > ttl) return null; // expired
    return data;
  } catch (e) {
    return null;
  }
}

// ── Canonical key builders ────────────────────────────────────────────────

export const CacheKeys = {
  authority:   (domain)           => `domainAuthority_${domain}`,
  pageSpeed:   (domain)           => `pageSpeedRaw_${domain}`,
  gmb:         (domain, location) => `gmbBrand_${domain}_${location}`,
  technical:   (domain)           => `technicalSEO_${domain}`,
  overallScore:(domain)           => `overallScore_${domain}`,
};
